import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Microsoft as MicrosoftIcon
} from '@mui/icons-material';
import microsoftAuthService from '../../services/microsoftAuth.service';
import microsoftGraphService from '../../services/microsoftGraph.service';
import { Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType, TableOfContents} from 'docx';
import { marked } from 'marked';

const HEADING_LINE_REGEX = /^(\d+(?:\.\d+)*)(?:\.)?\s+(.+)$/;
const HEADING_TRAILING_PUNCTUATION = /[.!?]$/;
const HEADING_FORBIDDEN_PATTERNS = [
  /\bmust\b/i,
  /\bshould\b/i,
  /\bwill\b/i,
  /\bshall\b/i,
];

const stripHeadingWrapper = (text = '') => text.replace(/^[\[{(<]+/, '').trim();

const deriveHeadingLevel = (numberPart = '') => {
  if (!numberPart) return 1;
  if (/\.0$/.test(numberPart)) return 1;
  return Math.min(numberPart.split('.').length, 6);
};

const parseNumberedHeading = (line = '') => {
  if (!line) return null;
  const trimmed = line.trim();
  if (!trimmed || /^#{1,6}\s/.test(trimmed)) return null;
  const match = trimmed.match(HEADING_LINE_REGEX);
  if (!match) return null;
  const [, numberPart, rawTitle] = match;
  const title = rawTitle.trim();
  if (!title) return null;
  const normalized = stripHeadingWrapper(title);
  if (!normalized) return null;
  const words = normalized.split(/\s+/).filter(Boolean);
  if (words.length > 18) return null;
  const firstChar = normalized.charAt(0);
  if (!/[A-Z]/.test(firstChar)) return null;
  if (HEADING_TRAILING_PUNCTUATION.test(normalized)) return null;
  const padded = ` ${normalized} `;
  if (HEADING_FORBIDDEN_PATTERNS.some((pattern) => pattern.test(padded))) {
    return null;
  }
  return {
    number: numberPart,
    title,
    level: deriveHeadingLevel(numberPart),
    normalizedTitle: normalized,
  };
};

const toDocxHeadingLevel = (numericLevel = 1) => {
  const level = Math.min(Math.max(numericLevel, 1), 6);
  return HeadingLevel[`HEADING_${level}`] || HeadingLevel.HEADING_1;
};

const MicrosoftWordDialog = ({ 
  open, 
  onClose, 
  sections, 
  trialSchemaImageUrl, 
  generatedSoAData, 
  fontConfig, 
  titlePageConfig 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [documentInfo, setDocumentInfo] = useState(null);
  const [sharingLink, setSharingLink] = useState(null);

  useEffect(() => {
    if (open) {
      checkMicrosoftAuth();
    }
  }, [open]);

  const checkMicrosoftAuth = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const authResult = await microsoftAuthService.initializeAuth();
      
      if (authResult.success) {
        setIsAuthenticated(true);
        microsoftGraphService.setAccessToken(authResult.token);
      } else if (authResult.needsAuth) {
        setIsAuthenticated(false);
      } else {
        setError(authResult.error);
      }
    } catch (error) {
      setError('Failed to check Microsoft authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    microsoftAuthService.startAuthFlow();
  };

  // Helper function to convert markdown to docx elements
  const markdownToDocxElements = (markdown, fontConfig) => {
    // Pre-process markdown to convert plain text headings to markdown headings
    let processedMarkdown = markdown || '';
    
    // Convert plain text headings to markdown headings
    // Pattern: lines that start with numbers like "1", "1.1", "1.1.1", etc.
    console.log('Original markdown:', processedMarkdown.substring(0, 500));
    
    // First, handle main section headings (single number like "1 PROTOCOL SUMMARY" or "1. PROTOCOL SUMMARY")
    processedMarkdown = processedMarkdown.replace(/^(\d+\.?\s+[^\n]+)$/gm, (match) => {
      const headingInfo = parseNumberedHeading(match);
      if (!headingInfo || headingInfo.level !== 1) {
        return match;
      }
      console.log(`Converting main heading: "${headingInfo.title}" to "# ${headingInfo.title}"`);
      return `# ${match.trim()}`;
    });
    
    // Then, handle subsection headings (multiple numbers like "1.1", "1.1.1")
    processedMarkdown = processedMarkdown.replace(/^(\d+\.\d+(?:\.\d+)?\s+[^\n]+)$/gm, (match) => {
      const headingInfo = parseNumberedHeading(match);
      if (!headingInfo || headingInfo.level < 2) {
        return match;
      }
      const prefix = '#'.repeat(Math.min(headingInfo.level, 6));
      console.log(`Converting sub heading: "${headingInfo.title}" to "${prefix} ${headingInfo.title}"`);
      return `${prefix} ${match.trim()}`;
    });
    
    console.log('Processed markdown:', processedMarkdown.substring(0, 500));
    
    const tokens = marked.lexer(processedMarkdown);
    console.log('Markdown tokens:', tokens.map(t => ({ type: t.type, text: t.text?.substring(0, 50) })));
    const elements = [];
    
    // If no headings were detected, try direct processing
    if (tokens.filter(t => t.type === 'heading').length === 0) {
      console.log('No headings detected in markdown, trying direct processing...');
      const lines = processedMarkdown.split('\n');
      for (const line of lines) {
        const headingInfo = parseNumberedHeading(line);
        if (headingInfo) {
          const headingFontConfig = headingInfo.level === 1 ? fontConfig.heading : fontConfig.subheading;
          const paragraph = new Paragraph({
            children: [
              new TextRun({
                text: line.trim(),
                font: headingFontConfig.name,
                size: headingFontConfig.size * 2,
                bold: headingFontConfig.bold,
                color: headingFontConfig.color
              })
            ],
            heading: toDocxHeadingLevel(headingInfo.level),
            alignment: AlignmentType.LEFT,
            spacing: {
              line: Math.round(headingFontConfig.lineSpacing * 240),
              after: headingInfo.level === 1 ? 240 : 180,
              before: 120
            }
          });
          elements.push(paragraph);
        } else if (line.trim()) {
          // Regular paragraph
          const paragraph = new Paragraph({
            children: [
              new TextRun({
                text: line,
                font: fontConfig.body.name,
                size: fontConfig.body.size * 2,
                bold: fontConfig.body.bold,
                color: fontConfig.body.color
              })
            ],
            alignment: AlignmentType.JUSTIFIED,
            spacing: {
              line: Math.round(fontConfig.body.lineSpacing * 240),
              before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
            }
          });
          console.log(`Created body paragraph with ${paragraph.children?.length || 0} children`);
          elements.push(paragraph);
        }
      }
      return elements;
    }
    
    for (const token of tokens) {
      if (token.type === 'heading') {
        const headingFontConfig = token.depth === 1 ? fontConfig.heading : fontConfig.subheading;
        const paragraph = new Paragraph({
          children: [
            new TextRun({
              text: token.text,
              font: headingFontConfig.name,
              size: headingFontConfig.size * 2,
              bold: headingFontConfig.bold,
              color: headingFontConfig.color
            })
          ],
          heading: toDocxHeadingLevel(token.depth),
          alignment: AlignmentType.LEFT,
          spacing: {
            line: Math.round(headingFontConfig.lineSpacing * 240),
            after: 240,
            before: 120 // Add space before headings for consistency
          }
        });
        console.log(`Created markdown heading paragraph with ${paragraph.children?.length || 0} children`);
        elements.push(paragraph);
      } else if (token.type === 'paragraph') {
        const paragraph = new Paragraph({
          children: [
            new TextRun({
              text: token.text,
              font: fontConfig.body.name,
              size: fontConfig.body.size * 2,
              bold: fontConfig.body.bold,
              color: fontConfig.body.color
            })
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            line: Math.round(fontConfig.body.lineSpacing * 240),
            before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
          }
        });
        console.log(`Created markdown paragraph with ${paragraph.children?.length || 0} children`);
        elements.push(paragraph);
      } else if (token.type === 'text') {
        const paragraph = new Paragraph({
          children: [
            new TextRun({
              text: token.text,
              font: fontConfig.body.name,
              size: fontConfig.body.size * 2,
              bold: fontConfig.body.bold,
              color: fontConfig.body.color
            })
          ],
          alignment: AlignmentType.JUSTIFIED,
          spacing: {
            line: Math.round(fontConfig.body.lineSpacing * 240),
            before: 60 // Reduced from 120 to 60 for better spacing (60 twips = 3pt)
          }
        });
        console.log(`Created markdown text paragraph with ${paragraph.children?.length || 0} children`);
        elements.push(paragraph);
      }
    }
    return elements;
  };

  // Helper function to generate title page content
  const generateTitlePageContent = (config) => {
    console.log('Generating title page with config:', config);
    
    if (!config.enabled) {
      console.log('Title page is disabled');
      return [];
    }
    
    const children = [];
    
    // Add proper spacing at the top
    children.push(new Paragraph({ 
      text: '',
      spacing: {
        after: 360 // Add 18pt space at the top (360 twips = 18pt)
      }
    }));
    
    // Add fields in the format shown in the template
    const fields = [
      'sponsorConfidentialityStatement',
      'fullTitle', 
      'trialAcronym',
      'sponsorProtocolIdentifier',
      'originalProtocol',
      'versionNumber',
      'versionDate',
      'amendmentIdentifier',
      'amendmentScope',
      'sponsorProductCodes',
      'investigationalProductNames',
      'trialPhase',
      'shortTitle'
    ];
    
    fields.forEach(fieldKey => {
      const field = config[fieldKey];
      console.log(`Processing field ${fieldKey}:`, field);
      
      if (field && field.enabled !== false) {
        const text = field.text || '';
        console.log(`Field ${fieldKey} enabled, text: "${text}"`);
        
        if (text.trim()) {
          children.push(
            new Paragraph({
              children: [
                // Label (bold)
                new TextRun({
                  text: field.label,
                  font: field.font || 'Times New Roman',
                  size: (field.size || 14) * 2,
                  bold: true, // Label is always bold
                  color: field.color || '000000'
                }),
                // Value (unbold)
                new TextRun({
                  text: ` ${text}`,
                  font: field.font || 'Times New Roman',
                  size: (field.size || 14) * 2,
                  bold: false, // Value is always unbold
                  color: field.color || '000000'
                })
              ],
              alignment: field.alignment === 'center' ? AlignmentType.CENTER : 
                        field.alignment === 'right' ? AlignmentType.RIGHT : AlignmentType.JUSTIFIED,
              spacing: {
                line: Math.round((field.lineSpacing || 1.0) * 240), // Convert to twips (240 = 1.0 spacing)
                before: 60, // Add space before title page fields (60 twips = 3pt)
                after: 120  // Add space after title page fields (120 twips = 6pt)
              }
            })
          );
        }
      } else {
        console.log(`Field ${fieldKey} is disabled or missing`);
      }
    });
    
    console.log(`Generated ${children.length} title page elements`);
    
    // Add proper spacing and page break
    children.push(new Paragraph({ 
      text: '',
      spacing: {
        before: 240 // Add 12pt space before page break (240 twips = 12pt)
      },
      pageBreakBefore: true 
    }));
    
    return children;
  };

  const generateWordDocument = async () => {
    try {
      const children = [];
      
      // Add title page if configured
      if (titlePageConfig && titlePageConfig.enabled) {
        children.push(...generateTitlePageContent(titlePageConfig));
      }
      
      // Add document title (if no title page or as fallback)
      if (!titlePageConfig || !titlePageConfig.enabled) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Clinical Trial Protocol",
                font: fontConfig.title.name,
                size: fontConfig.title.size * 2,
                bold: fontConfig.title.bold,
                color: fontConfig.title.color
              })
            ],
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: {
              line: Math.round(fontConfig.title.lineSpacing * 240),
              after: 360
            }
          }),
          new Paragraph({ text: '' })
        );
      }

      children.push(new Paragraph({ children: [], pageBreakBefore: true })); // optional page break before TOC

// TOC title
children.push(
  new Paragraph({
    children: [
      new TextRun({
        text: "Table of Contents",
        bold: true,
      })
    ],
    heading: HeadingLevel.HEADING_1,
    alignment: AlignmentType.CENTER,
    spacing: { after: 480 }
  })
);

// Native Word TOC field (levels 1–6, with hyperlinks)
children.push(
  new TableOfContents("", {
    hyperlink: true,
    headingStyleRange: "1-6",
  })
);

// page break after TOC
children.push(new Paragraph({ children: [], pageBreakBefore: true }));
// --- INSERT THIS BLOCK (end) ---
      
      // Process sections
      let _encounteredAnySection = false;
      for (const section of sections) {
        if (section.output && section.output.trim()) {
          if (_encounteredAnySection) {
            // Start each section on a new page (skip before the first)
            children.push(new Paragraph({ children: [], pageBreakBefore: true }));
          }
          // Always add the section title as a real Word heading so it appears in the TOC.
          // Many generated section bodies don't repeat the section title as a heading.
          const parsedTitleHeading = parseNumberedHeading(section.title);
          const titleHeadingLevel = parsedTitleHeading?.level ?? 1;
          const titleHeadingFont =
            titleHeadingLevel === 1 ? fontConfig.heading : fontConfig.subheading;

          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: section.title,
                  font: titleHeadingFont.name,
                  size: titleHeadingFont.size * 2,
                  bold: titleHeadingFont.bold,
                  color: titleHeadingFont.color,
                }),
              ],
              heading: toDocxHeadingLevel(titleHeadingLevel),
              alignment: AlignmentType.LEFT,
              spacing: {
                line: Math.round(titleHeadingFont.lineSpacing * 240),
                after: titleHeadingLevel === 1 ? 240 : 180,
                before: 120,
              },
            })
          );
          
          // Process section content
          const docxElements = markdownToDocxElements(section.output, fontConfig);
          if (docxElements.length > 0) {
            children.push(...docxElements);
          }
          
          children.push(new Paragraph({ text: '' }));
          _encounteredAnySection = true;
        }
      }
      
      const doc = new Document({
        sections: [{ children }],
      });
      
      return await Packer.toBlob(doc);
    } catch (error) {
      console.error('Error generating Word document:', error);
      throw error;
    }
  };

  const handleOpenInMicrosoftWord = async () => {
    setLoading(true);
    setError(null);
    setUploadStatus('generating');
    
    try {
      // Generate Word document
      const wordBlob = await generateWordDocument();
      setUploadStatus('opening');
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(wordBlob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `Clinical_Trial_Protocol_${new Date().toISOString().split('T')[0]}.docx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL
      window.URL.revokeObjectURL(url);
      
      setUploadStatus('success');
      setDocumentInfo({
        fileName: `Clinical_Trial_Protocol_${new Date().toISOString().split('T')[0]}.docx`,
        downloaded: true
      });
      
    } catch (error) {
      console.error('Error generating Word document:', error);
      setError('Failed to generate Word document');
      setUploadStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInWordOnline = async () => {
    try {
      // Generate Word document
      const wordBlob = await generateWordDocument();
      
      // Upload to OneDrive temporarily for Word Online
      const fileName = `Clinical_Trial_Protocol_${new Date().toISOString().split('T')[0]}.docx`;
      const uploadResult = await microsoftGraphService.uploadToOneDrive(fileName, wordBlob);
      
      if (uploadResult.success) {
        const result = await microsoftGraphService.openInWordOnline(uploadResult.fileId);
        if (result.success) {
          window.open(result.wordOnlineUrl, '_blank');
        } else {
          setError('Failed to open document in Word Online');
        }
      } else {
        setError('Failed to upload document for Word Online');
      }
    } catch (error) {
      setError('Failed to open document in Word Online');
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'generating':
        return <CircularProgress size={20} />;
      case 'uploading':
        return <CloudUploadIcon color="primary" />;
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (uploadStatus) {
      case 'generating':
        return 'Generating Word document...';
      case 'opening':
        return 'Opening in Microsoft Word...';
      case 'success':
        return 'Document generated successfully!';
      case 'error':
        return 'Generation failed';
      default:
        return '';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5'
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          <MicrosoftIcon sx={{ color: '#0078d4' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#0078d4' }}>
            Microsoft Word Integration
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
        {loading && !uploadStatus ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Checking Microsoft authentication...</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !isAuthenticated ? (
          <Box textAlign="center" py={4}>
            <MicrosoftIcon sx={{ fontSize: 64, color: '#0078d4', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Connect to Microsoft Word
            </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
               Sign in with your Microsoft account to generate Word documents and open them in Microsoft Word.
             </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<MicrosoftIcon />}
              onClick={handleMicrosoftLogin}
              sx={{
                backgroundColor: '#0078d4',
                '&:hover': { backgroundColor: '#106ebe' }
              }}
            >
              Sign in with Microsoft
            </Button>
          </Box>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">
                Connected to Microsoft
              </Typography>
              <Chip label="Authenticated" color="success" size="small" />
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            {!documentInfo ? (
                           <Box textAlign="center" py={4}>
               <MicrosoftIcon sx={{ fontSize: 64, color: '#0078d4', mb: 2 }} />
               <Typography variant="h6" gutterBottom>
                 Generate Word Document
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                 Generate a Word document from your protocol sections and download it to open in Microsoft Word.
               </Typography>
               <Button
                 variant="contained"
                 size="large"
                 startIcon={<CloudUploadIcon />}
                 onClick={handleOpenInMicrosoftWord}
                 disabled={loading}
                 sx={{
                   backgroundColor: '#0078d4',
                   '&:hover': { backgroundColor: '#106ebe' }
                 }}
               >
                 Generate & Download
               </Button>
             </Box>
            ) : (
              <Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  {getStatusIcon()}
                  <Typography variant="h6">
                    {getStatusText()}
                  </Typography>
                </Box>
                
                                 <List>
                   <ListItem>
                     <ListItemIcon>
                       <MicrosoftIcon color="primary" />
                     </ListItemIcon>
                     <ListItemText 
                       primary="Document Name" 
                       secondary={documentInfo.fileName}
                     />
                   </ListItem>
                   
                   <ListItem>
                     <ListItemIcon>
                       <MicrosoftIcon color="primary" />
                     </ListItemIcon>
                     <ListItemText 
                       primary="Microsoft Word" 
                       secondary="Open document in Microsoft Word"
                     />
                     <Button
                       variant="outlined"
                       size="small"
                       onClick={handleOpenInMicrosoftWord}
                     >
                       Download Again
                     </Button>
                   </ListItem>
                   
                   <ListItem>
                     <ListItemIcon>
                       <MicrosoftIcon color="primary" />
                     </ListItemIcon>
                     <ListItemText 
                       primary="Word Online" 
                       secondary="Edit document in Word Online"
                     />
                     <Button
                       variant="outlined"
                       size="small"
                       onClick={handleOpenInWordOnline}
                     >
                       Open Online
                     </Button>
                   </ListItem>
                 </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f5f5f5',
        p: 2
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Close
        </Button>
                 {isAuthenticated && !documentInfo && (
           <Button
             onClick={handleOpenInMicrosoftWord}
             variant="contained"
             startIcon={<MicrosoftIcon />}
             disabled={loading}
             sx={{
               backgroundColor: '#0078d4',
               '&:hover': { backgroundColor: '#106ebe' }
             }}
           >
             Generate & Download
           </Button>
         )}
      </DialogActions>
    </Dialog>
  );
};

export default MicrosoftWordDialog;
